import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { GapSettings } from '../../config/gapsettings';
import { RegistrationService } from '../../services/registration.service';

/**
 * Resolves the effective disable_nhs_login flag.
 * Precedence: GapSettings (Dexie-backed) → localStorage fallback → '0'.
 * Mirrors CreateAccountRedirectGuard and DataService.disable_nhs_login.
 */
function isNhsLoginDisabled(gapsettings: GapSettings): boolean {
  const fromSettings = gapsettings.disable_nhs_login;
  if (fromSettings === '1' || fromSettings === '0') {
    return fromSettings === '1';
  }
  return localStorage.getItem('disable_nhs_login') === '1';
}

/**
 * registrationGuard — protects multi-step registration flow.
 *
 * Checks:
 *   1. Registration has an active temp_code (RegistrationService.current_temp_code
 *      via Dexie, with localStorage 'registerobject' fallback for legacy).
 *   2. Respects disable_nhs_login flag for NHS-based registration branches.
 *
 * When blocked:
 *   - No temp_code → redirect to /postcode-entry (start of registration)
 *   - NHS disabled and route is NHS-only → redirect to /create-account-manual
 *
 * Async because RegistrationService is Dexie-backed (Promise).
 */
export const registrationGuard: CanActivateFn = async (route, state) => {
  const gapsettings = inject(GapSettings);
  const registrationService = inject(RegistrationService);
  const router = inject(Router);

  const isDisabled = isNhsLoginDisabled(gapsettings);

  const isNhsOnlyStep = route.data?.['nhsOnly'] === true;
  if (isNhsOnlyStep && isDisabled) {
    return router.createUrlTree(['/create-account-manual']);
  }

  let tempCode: string | null = null;
  try {
    tempCode = await registrationService.getCurrentTempCode();
  } catch {
    tempCode = null;
  }

  if (!tempCode) {
    try {
      const raw = localStorage.getItem('registerobject');
      if (raw) {
        const parsed = JSON.parse(raw);
        tempCode = parsed?.tempRegistrationCode ?? parsed?.temp_registration_code ?? null;
      }
    } catch {
      tempCode = null;
    }
  }

  if (!tempCode) {
    const legacyTemp = localStorage.getItem('tempRegistrationCode');
    if (legacyTemp) {
      tempCode = legacyTemp;
    }
  }

  if (!tempCode) {
    return router.createUrlTree(['/get-started']);
  }

  return true;
};
